import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useGlobalSSE } from './GlobalSSEContext';
import { fetchV3TabData } from '../services/api';
import useAuth from '../hooks/useAuth';

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  UPDATE_WIDGETS: 'UPDATE_WIDGETS',
  SET_TAB_DATA: 'SET_TAB_DATA',
  CLEAR_TAB_DATA: 'CLEAR_TAB_DATA',
};

// Initial state
const initialState = {
  hero: {
    widgets: [],
    data: null,
    isLoading: true,
    error: null,
  },
  inventory: {
    widgets: [],
    isLoading: true,
    error: null,
  },
  guild: {
    widgets: [],
    isLoading: true,
    error: null,
  },
  feed: {
    widgets: [],
    isLoading: true,
    error: null,
  },
  activeTab: null,
};

// Reducer
function v3DataReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        [action.tab]: {
          ...state[action.tab],
          isLoading: action.isLoading,
        },
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        [action.tab]: {
          ...state[action.tab],
          error: action.error,
          isLoading: false,
        },
      };

    case ActionTypes.SET_TAB_DATA:
      return {
        ...state,
        [action.tab]: {
          ...state[action.tab],
          data: action.data,
          isLoading: false,
          error: null,
        },
      };

    case ActionTypes.UPDATE_WIDGETS:
      const currentWidgets = state[action.tab].widgets;
      const widgetMap = new Map(currentWidgets.map(w => [w.id, w]));
      
      // Debug logging for inventory updates
      if (action.tab === 'inventory') {
        console.log('[V3DataContext Reducer] UPDATE_WIDGETS for inventory');
        console.log('[V3DataContext Reducer] Current widgets:', currentWidgets);
        console.log('[V3DataContext Reducer] New widgets:', action.widgets);
      }
      
      // Update or add widgets - create new objects to ensure React detects changes
      action.widgets.forEach(widget => {
        // Always replace with new widget object to trigger React updates
        widgetMap.set(widget.id, { ...widget });
      });

      const newWidgets = Array.from(widgetMap.values());
      
      if (action.tab === 'inventory') {
        console.log('[V3DataContext Reducer] Final widgets after update:', newWidgets);
      }

      return {
        ...state,
        [action.tab]: {
          ...state[action.tab],
          widgets: newWidgets,
          isLoading: false,
          error: null,
        },
      };

    case ActionTypes.CLEAR_TAB_DATA:
      return {
        ...state,
        [action.tab]: {
          widgets: [],
          isLoading: true,
          error: null,
        },
      };

    default:
      return state;
  }
}

// Context
const V3DataContext = createContext(null);

// Provider component
export function V3DataProvider({ children }) {
  const [state, dispatch] = useReducer(v3DataReducer, initialState);
  const { registerHandler, unregisterHandler } = useGlobalSSE();
  
  // Register handlers for inventory, hero, and guild tabs
  useEffect(() => {
    const handleTabEvent = (tab) => (eventType, data) => {
      console.log(`[V3DataContext] Received ${eventType} for ${tab}`);
      
      if (eventType === `v3${tab.charAt(0).toUpperCase() + tab.slice(1)}`) {
        // Handle v3Update format
        if (data && data.type === 'full' && data.data) {
          // New v3 format with structured data
          dispatch({
            type: ActionTypes.SET_TAB_DATA,
            tab,
            data: data.data,
          });
        } else if (Array.isArray(data)) {
          // Legacy widget format
          const widgets = typeof data === 'string' ? JSON.parse(data) : data;
          const convertedWidgets = widgets.map(widget => {
            if (widget.key && widget.value) {
              return {
                id: widget.key,
                type: widget.value.type || 'unknown',
                data: widget.value,
              };
            }
            return widget;
          });
          
          dispatch({
            type: ActionTypes.UPDATE_WIDGETS,
            tab,
            widgets: convertedWidgets,
          });
        }
      }
    };
    
    // Register handlers for each tab
    // Note: guild and inventory are handled by their own components (V3GuildTabNew, V3InventoryTab)
    registerHandler('v3_hero', handleTabEvent('hero'));
    
    return () => {
      unregisterHandler('v3_hero');
    };
  }, [registerHandler, unregisterHandler]);

  // Connect to SSE for a specific tab
  const connectTab = useCallback(async (tabName, token) => {
    if (!token) {
      dispatch({
        type: ActionTypes.SET_ERROR,
        tab: tabName,
        error: 'No auth token',
      });
      return;
    }

    // Map tab names to internal keys
    const tabKey = tabName.replace('v3_', '').replace('_v3', '');
    
    // Clear existing data and set loading
    dispatch({ type: ActionTypes.CLEAR_TAB_DATA, tab: tabKey });

    // Tab change is handled by GlobalSSEProvider

    // Then immediately fetch data for the tab
    try {
      console.log('[V3DataContext] Fetching initial data for tab:', tabName);
      const data = await fetchV3TabData(token, tabName);
      
      console.log('[V3DataContext] Received tab data:', data);
      
      // Process the data based on tab type
      if (data && data.widgets) {
        // Convert widgets from backend format if needed
        const convertedWidgets = data.widgets.map(widget => {
          if (widget.key && widget.value) {
            // Legacy format: {key: "P1", value: {type: "...", ...}}
            return {
              id: widget.key,
              type: widget.value.type || 'unknown',
              data: widget.value
            };
          }
          // Already in correct format
          return widget;
        });
        
        dispatch({
          type: ActionTypes.UPDATE_WIDGETS,
          tab: tabKey,
          widgets: convertedWidgets,
        });
      }
    } catch (error) {
      console.error('[V3DataContext] Failed to fetch tab data:', error);
      dispatch({
        type: ActionTypes.SET_ERROR,
        tab: tabKey,
        error: error.message || 'Failed to load data',
      });
    }
  }, []);

  // Disconnect from SSE for a specific tab
  const disconnectTab = useCallback((tabName) => {
    // Do not disconnect - we're using a single SSE connection
    console.log('[V3DataContext] NOT disconnecting SSE for', tabName, '- using main connection');
  }, []);

  // Cleanup is handled by unregisterHandler in the previous useEffect

  const value = {
    state,
    connectTab,
    disconnectTab,
  };

  return (
    <V3DataContext.Provider value={value}>
      {children}
    </V3DataContext.Provider>
  );
}

// Custom hook to use V3 data
export function useV3Data() {
  const context = useContext(V3DataContext);
  if (!context) {
    throw new Error('useV3Data must be used within V3DataProvider');
  }
  return context;
}

// Specific hooks for each tab
export function useV3HeroData() {
  const { state, connectTab, disconnectTab } = useV3Data();
  const { token } = useAuth();

  useEffect(() => {
    if (token) {
      connectTab('v3_hero', token);
    }
    return () => {
      disconnectTab('v3_hero');
    };
  }, [token, connectTab, disconnectTab]);

  return state.hero;
}

export function useV3InventoryData() {
  const { state, connectTab, disconnectTab } = useV3Data();
  const { token } = useAuth();

  useEffect(() => {
    if (token) {
      connectTab('v3_inventory', token);
    }
    return () => {
      disconnectTab('v3_inventory');
    };
  }, [token, connectTab, disconnectTab]);

  return state.inventory;
}

export function useV3GuildData() {
  const { state, connectTab, disconnectTab } = useV3Data();
  const { token } = useAuth();

  useEffect(() => {
    if (token) {
      connectTab('v3_guild', token);
    }
    return () => {
      disconnectTab('v3_guild');
    };
  }, [token, connectTab, disconnectTab]);

  return state.guild;
}

export function useV3FeedData() {
  const { state, connectTab, disconnectTab } = useV3Data();
  const { token } = useAuth();

  useEffect(() => {
    if (token) {
      connectTab('feed_v3', token);
    }
    return () => {
      disconnectTab('feed_v3');
    };
  }, [token, connectTab, disconnectTab]);

  return state.feed;
}